// Phase E1 — Evidence Drawer (presentation only).
//
// Derives a read-only display model from data that is ALREADY present on a
// finding: `triggering_data` (populated by the rule engines) and the
// normalized `room_context`. It NEVER invents evidence, never reads parsed
// estimate data, and never changes a finding. Each triggering_data key is
// consumed by exactly one section (no duplication). When nothing is available
// `buildEvidence` returns null so the "View Evidence" button is hidden.
//
// Sections produced (matching the Phase E1 spec):
//   1. Room Evidence            — flagged_rooms, wet_rooms, rooms, room_detail,
//                                 room_context
//   2. Supporting Line Items    — duplicates / line refs / descriptions → table
//   3. Quantities / Measurements — top-level numeric scalars → labeled metrics
//   4. Generic Fallback         — any remaining key → key/value pairs

// triggering_data keys whose values describe ROOMS.
const ROOM_LIST_KEYS = ['flagged_rooms', 'wet_rooms', 'rooms', 'room_detail'];

// Sub-keys treated as an item description on an object entry (item-focused —
// deliberately excludes room/area so a line item's description is the item
// text, not its location).
const DESCRIPTION_KEYS = [
    'description', 'desc', 'name', 'item', 'vendor', 'equipment',
    'classification', 'category', 'type', 'issue', 'label',
];

// Sub-keys treated as a line / position reference on an object entry.
const LINE_KEYS = ['line', 'line_number', 'lineNumber', 'line_numbers', 'lineNumbers'];

// Sub-keys used to pull a room name off a room object.
const ROOM_NAME_KEYS = ['room', 'roomName', 'room_name', 'name', 'area', 'areaName'];

const MAX_ROWS = 12;  // cap long tables; remainder surfaced as "+N more"
const MAX_NAMES = 8;  // cap inline name lists

function humanizeKey(key) {
    return String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim()
        .replace(/^./, c => c.toUpperCase());
}

function isScalar(v) {
    return v === null || ['string', 'number', 'boolean'].includes(typeof v);
}

function formatScalar(v) {
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') {
        return Number.isFinite(v) ? v.toLocaleString('en-US') : String(v);
    }
    return String(v);
}

function pickFirst(obj, keys) {
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return undefined;
}

function capRows(arr) {
    if (arr.length <= MAX_ROWS) return { rows: arr, more: 0 };
    return { rows: arr.slice(0, MAX_ROWS), more: arr.length - MAX_ROWS };
}

// Pull room names out of a value that may be an array of strings, an array of
// room objects, or a single string.
function extractRoomNames(value) {
    if (!value) return [];
    const arr = Array.isArray(value) ? value : [value];
    const names = [];
    arr.forEach(entry => {
        if (isScalar(entry)) {
            if (entry !== null && String(entry).trim() !== '') names.push(String(entry));
        } else if (entry && typeof entry === 'object') {
            const name = pickFirst(entry, ROOM_NAME_KEYS);
            if (name !== undefined) names.push(String(name));
        }
    });
    return names;
}

// Build a compact table model from an array. Handles arrays of objects (Line /
// Description columns plus a column per remaining scalar field) and arrays of
// bare strings (single "Item" column). Returns null when nothing renders.
function buildTable(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const objs = arr.filter(e => e && typeof e === 'object' && !Array.isArray(e));

    if (objs.length === 0) {
        const strs = arr.filter(e => isScalar(e) && e !== null && String(e).trim() !== '').map(String);
        if (strs.length === 0) return null;
        const { rows, more } = capRows(strs);
        return { columns: ['Item'], rows: rows.map(s => [s]), more };
    }

    const hasLine = objs.some(o => pickFirst(o, LINE_KEYS) !== undefined);
    const hasDesc = objs.some(o => pickFirst(o, DESCRIPTION_KEYS) !== undefined);
    const used = new Set([...LINE_KEYS, ...DESCRIPTION_KEYS]);

    const extra = [];
    objs.forEach(o => Object.entries(o).forEach(([k, v]) => {
        if (!used.has(k) && isScalar(v) && !extra.includes(k)) extra.push(k);
    }));

    const columns = [
        ...(hasLine ? ['Line'] : []),
        ...(hasDesc ? ['Description'] : []),
        ...extra.map(humanizeKey),
    ];

    const { rows: capped, more } = capRows(objs);
    const rows = capped.map(o => {
        const cells = [];
        if (hasLine) {
            const l = pickFirst(o, LINE_KEYS);
            cells.push(l === undefined ? '' : (Array.isArray(l) ? l.join(', ') : formatScalar(l)));
        }
        if (hasDesc) {
            const d = pickFirst(o, DESCRIPTION_KEYS);
            cells.push(d === undefined ? '' : String(d));
        }
        extra.forEach(k => {
            const v = o[k];
            cells.push(isScalar(v) && v !== null ? formatScalar(v) : '');
        });
        return cells;
    });

    return { columns, rows, more };
}

// Short inline summary of an array for the Generic Fallback section.
function summarizeArray(arr) {
    const names = [];
    arr.forEach(e => {
        if (isScalar(e)) {
            if (e !== null && String(e).trim() !== '') names.push(String(e));
        } else if (e && typeof e === 'object') {
            const n = pickFirst(e, [...ROOM_NAME_KEYS, ...DESCRIPTION_KEYS]);
            if (n !== undefined) names.push(String(n));
        }
    });
    if (names.length === 0) return `${arr.length} ${arr.length === 1 ? 'entry' : 'entries'}`;
    const shown = names.slice(0, MAX_NAMES);
    const more = names.length - shown.length;
    return shown.join(', ') + (more > 0 ? ` (+${more} more)` : '');
}

// ── Section builders (each marks the keys it consumes) ──────────────────────

// 1. Room Evidence — room_context plus any room-list keys.
function buildRoomEvidence(finding, consumed) {
    const td = finding.triggering_data || {};
    const rc = finding.room_context;
    const context = rc && rc.label ? rc.label : null;

    const groups = [];
    ROOM_LIST_KEYS.forEach(key => {
        if (!(key in td)) return;
        const names = extractRoomNames(td[key]);
        if (names.length === 0) return;
        const shown = names.slice(0, MAX_NAMES);
        const more = names.length - shown.length;
        groups.push({
            label: humanizeKey(key),
            value: shown.join(', ') + (more > 0 ? ` (+${more} more)` : ''),
        });
        consumed.add(key);
    });

    if (!context && groups.length === 0) return null;
    return { context, groups };
}

// 2. Supporting Line Items — qualifying arrays rendered as compact tables.
function buildLineItems(finding, consumed) {
    const td = finding.triggering_data || {};
    const tables = [];

    Object.entries(td).forEach(([key, value]) => {
        if (consumed.has(key)) return;
        if (!Array.isArray(value) || value.length === 0) return;

        const objs = value.filter(e => e && typeof e === 'object');
        const qualifies = key === 'duplicates'
            || objs.some(o => pickFirst(o, LINE_KEYS) !== undefined)
            || objs.some(o => pickFirst(o, DESCRIPTION_KEYS) !== undefined);
        if (!qualifies) return;

        const table = buildTable(value);
        if (table) {
            tables.push({ caption: humanizeKey(key), table });
            consumed.add(key);
        }
    });

    return tables.length ? tables : null;
}

// 3. Quantities / Measurements — top-level numeric scalars as labeled metrics.
function buildMetrics(finding, consumed) {
    const td = finding.triggering_data || {};
    const metrics = [];

    Object.entries(td).forEach(([key, value]) => {
        if (consumed.has(key)) return;
        if (typeof value !== 'number' || !Number.isFinite(value)) return;
        metrics.push({ label: humanizeKey(key), value: formatScalar(value) });
        consumed.add(key);
    });

    return metrics.length ? metrics : null;
}

// 4. Generic Fallback — every remaining key as a key/value pair.
function buildFallback(finding, consumed) {
    const td = finding.triggering_data || {};
    const rows = [];

    Object.entries(td).forEach(([key, value]) => {
        if (consumed.has(key)) return;
        if (isScalar(value)) {
            if (value === null || value === '') return;
            rows.push({ label: humanizeKey(key), value: formatScalar(value) });
            consumed.add(key);
        } else if (Array.isArray(value)) {
            if (value.length === 0) return;
            rows.push({ label: humanizeKey(key), value: summarizeArray(value) });
            consumed.add(key);
        } else if (value && typeof value === 'object') {
            Object.entries(value).forEach(([k, v]) => {
                if (isScalar(v) && v !== null && v !== '') {
                    rows.push({ label: `${humanizeKey(key)} — ${humanizeKey(k)}`, value: formatScalar(v) });
                }
            });
            consumed.add(key);
        }
    });

    return rows.length ? rows : null;
}

// Build the full evidence display model for a finding. Returns null when there
// is nothing to show so the caller can hide the "View Evidence" affordance.
export function buildEvidence(finding) {
    if (!finding) return null;
    const consumed = new Set();
    const roomEvidence = buildRoomEvidence(finding, consumed);
    const lineItems = buildLineItems(finding, consumed);
    const metrics = buildMetrics(finding, consumed);
    const fallback = buildFallback(finding, consumed);

    if (!roomEvidence && !lineItems && !metrics && !fallback) return null;
    return { roomEvidence, lineItems, metrics, fallback };
}
